import type { Subscription } from '@/types/api';

/**
 * Whether access has already run out, as of now.
 *
 * Read from `access_ends_at`, not the term's end: a subscription the payment provider renews keeps
 * working for a grace period after its billing date while the renewal is collected, and calling it
 * lapsed then offered a second checkout for a plan that was being renewed.
 */
export function hasLapsed(subscription: Subscription): boolean {
  const ends = subscription.access_ends_at ?? subscription.current_period_end;

  return subscription.billing_interval !== 'lifetime' && ends !== null && new Date(ends) <= new Date();
}

/**
 * What the period end date means, which depends on the state it is read in.
 *
 * A bare "renews {date}" is a promise, and it was being made to subscriptions that will do no
 * such thing: a suspended one is on hold, a date already in the past has been and gone, and one an
 * administrator granted has nobody to charge. Shared by the customer and admin screens because it
 * was wrong in both, separately.
 */
export function periodSummary(subscription: Subscription): string {
  if (subscription.billing_interval === 'lifetime') return 'Lifetime. Never renews';

  const end = subscription.current_period_end;
  if (end === null) return 'Awaiting payment';

  const on = new Date(end).toLocaleDateString();

  // A free trial never renews, so "Renews {date}" was a promise it does not make.
  if (subscription.free_trial) {
    if (subscription.canceled_at !== null) return `Ended ${new Date(subscription.canceled_at).toLocaleDateString()}`;
    if (subscription.effective_status === 'suspended') return `On hold · trial runs to ${on}`;
    if (hasLapsed(subscription)) return `Ended ${on}`;

    return `Trial ends ${on}`;
  }

  if (subscription.effective_status === 'suspended') return `On hold · term runs to ${on}`;

  const accessEnds = subscription.access_ends_at === null ? on : new Date(subscription.access_ends_at).toLocaleDateString();

  if (hasLapsed(subscription)) return `Ended ${accessEnds}`;

  // The provider is retrying a renewal that failed; everything keeps working until the grace ends.
  if (subscription.effective_status === 'past_due') return `Payment overdue · retried until ${accessEnds}`;
  if (subscription.awaiting_renewal) return `Renewal in progress since ${on}`;
  if (subscription.cancel_at_period_end) return `Ends ${on}`;

  return subscription.renews_automatically ? `Renews ${on}` : `Ends ${on}`;
}
